/**
 * OL page registry - the single source of truth for the dashboard's pages.
 *
 * This list was previously inline in `pages/Dashboard.js`. It moved here on
 * 2026-10-16 so three things can share one definition:
 *   - the dashboard nav (role filter + feature gate),
 *   - `catalogPayload()`, which seeds the `ol_page_catalog` collection so
 *     Semprion can turn a page off for one organization without a deploy,
 *   - the cold-start seed the backend ships.
 *
 * ORDER MATTERS. A row's index within its role is its `sort_order`, exactly as
 * in BackOfficePro - which also means THIS ARRAY is the only place to reorder a
 * menu. Editing `sort_order` in Mongo looks like it works and then reverts on
 * the next sync; BOP made that mistake three times (see
 * BOP2/memory/docs/DVC_DYNAMIC_VIEW_CONFIGURATION.md section 0).
 *
 * Row shape: { value, label, roles, testid?, activeClass?, hideWhenAssistant? }
 * - unchanged from when it lived in Dashboard.js. `value` is the stable page id.
 * Category and feature gate are attached separately in PAGE_META below, so the
 * rows themselves (and the comments recording AJ's ordering decisions) stay
 * exactly as they were.
 */

// Role codes - must match `users.roles[].role_type`.
export const CS = 'company_support', LA = 'league_admin', AD = 'ad', TS = 'tech_specialist',
  TR = 'treasurer', AC = 'accountant', SCH = 'scheduler', CO = 'coach', FAM = 'family';

export const NAV_ITEMS = [
  { value: 'overview',     label: 'Overview',        roles: [CS, LA, AD, TR, AC, CO, FAM] },
  // Scheduler's first tab (AJ 2026-10-14); after Overview so the Coach order is unchanged.
  { value: 'schedule',     label: 'Schedule',        roles: [SCH, CO], hideWhenAssistant: true },
  // AD: Organizations is the Overview page's 2nd tab, not a menu (AJ 2026-10-19).
  { value: 'organizations',label: 'Organizations',   roles: [CS, LA] },
  // Teams = Org Teams + Opponents sub-tabs (AJ 2026-10-14; was two top-level menus).
  { value: 'teams-hub',    label: 'Teams',     roles: [CS, LA, AD, SCH], testid: 'tab-teams-hub' },
  // Media = home carousel + team photos + sponsors (MEDIA_PAGE.md); TS has the same screens in Website Manager.
  { value: 'site-media',   label: 'Media',     roles: [AD, SCH], testid: 'tab-site-media' },
  // Contacts = per-sport AD / opponent contact lists in editable sections (CONTACTS_PAGE.md).
  { value: 'contacts',     label: 'Contacts',  roles: [AD, SCH], testid: 'tab-contacts' },
  // Scheduler has Registration and every sub-page, the same as the AD (AJ 2026-10-18).
  { value: 'registration-hub', label: 'Registration', roles: [CS, AD, TS, SCH], testid: 'tab-registration-hub' },
  // Draft Board + Family Directory live under Registration for AD / Scheduler / HQ (AJ 2026-10-14, 10-18).
  { value: 'players',      label: 'My Players',      roles: [FAM] },
  { value: 'registration', label: 'Register', roles: [FAM] },
  { value: 'seasons',      label: 'Seasons',         roles: [CS], testid: 'tab-seasons' },
  { value: 'activity',     label: 'Activity Log',    roles: [CS, LA], testid: 'tab-activity' },
  // SCHEDULER: 'schedule' renders SchedulerHub (one tab per sport). RSVP /
  // Availability / Messaging moved INSIDE each sport tab, so SCH is no longer
  // listed on them below; 'schedule-v3' (WP Mgmt) stays top-level because it is
  // sport-independent. See SCHEDULER_ROLE_PAGE.md.
  { value: 'schedule-v3',  label: 'WP Mgmt',         roles: [CS, TS], testid: 'tab-schedule-v3' },   // Scheduler uses Org Teams / Opponents / Media instead (AJ 2026-10-14)
  { value: 'booking',      label: 'Booking Calendar',roles: [CS], testid: 'tab-booking' },
  { value: 'gameday',      label: 'Game Day',        roles: [CS, CO] },
  { value: 'roster',       label: 'Roster',          roles: [CS, LA, CO] },
  { value: 'rsvp',         label: 'RSVP',            roles: [CS, CO, FAM] },
  { value: 'availability', label: 'Availability',    roles: [CS, CO, FAM], hideWhenAssistant: true, testid: 'tab-availability' },
  { value: 'payments',     label: 'Payments',        roles: [CS, LA, FAM] },
  { value: 'exports',      label: 'Exports',         roles: [CS, LA] },
  { value: 'reports',      label: 'Reports',         roles: [CS, LA] },
  { value: 'messaging',    label: 'Messaging',       roles: [CS, LA, CO, FAM], hideWhenAssistant: true, testid: 'tab-messaging' },
  // Player paperwork alerts (physicals, waivers…). Was labelled "Library"; renamed
  // 2026-10-19 so it is not confused with the new Library (program documents).
  { value: 'documents',    label: 'Player Documents', roles: [CS, LA, AD] },
  // Library — the org's program documents as quick links (LIBRARY.md, AJ 2026-10-19).
  { value: 'library',      label: 'Library',         roles: [CS, AD], testid: 'tab-library' },
  { value: 'volunteer',    label: 'Volunteer',       roles: [CS, LA, AD, CO, FAM] },
  { value: 'families',     label: 'Family Directory', roles: [LA, CO] },   // AD/HQ: Registration ▸ Family Directory
  { value: 'teams',        label: 'Teams',           roles: [CO], hideWhenAssistant: true },
  { value: 'stats',        label: 'Statistics',      roles: [CS, LA, AD, CO] },
  { value: 'calendar',     label: 'Calendar',        roles: [CS, LA, AD, TS, CO, FAM] },
  { value: 'lineup',       label: 'Lineup',          roles: [CS, CO], hideWhenAssistant: true, testid: 'tab-lineup' },
  { value: 'live-scoring', label: 'Live Scoring',    roles: [CS, CO], hideWhenAssistant: true, testid: 'tab-live-scoring' },
  { value: 'media',        label: 'Media',           roles: [CS, CO], hideWhenAssistant: true, testid: 'tab-media' },
  { value: 'tournaments',  label: 'Tournaments',     roles: [CS], testid: 'tab-tournaments' },
  { value: 'sponsors',     label: 'Sponsors',        roles: [CS, TS], testid: 'tab-sponsors' },
  { value: 'settings',     label: 'Settings',        roles: [LA] },
  { value: 'hq-settings',  label: 'Settings',        roles: [CS], testid: 'tab-hq-settings' },
  { value: 'tech-settings',label: 'Website',   roles: [CS, TS], testid: 'tab-tech-settings', activeClass: 'data-[state=active]:bg-teal-600' },
  { value: 'member-mgmt',  label: 'Members',         roles: [CS, TS, LA], testid: 'tab-member-mgmt', activeClass: 'data-[state=active]:bg-teal-600' },
  { value: 'website-visibility', label: 'Website Visibility', roles: [CS, TS], testid: 'tab-website-visibility', activeClass: 'data-[state=active]:bg-teal-600' },
  { value: 'download-manager', label: 'Download Manager', roles: [CS, TS], testid: 'tab-download-manager', activeClass: 'data-[state=active]:bg-teal-600' },
  { value: 'individual-awards', label: 'Individual Awards', roles: [CS, TS], testid: 'tab-individual-awards', activeClass: 'data-[state=active]:bg-teal-600' },
  // Post News for Scheduler + AD (AJ, 2026-10-11): the SAME Post News screen the
  // Tech Specialist uses, including the Website/Facebook destination boxes.
  // They do NOT get Tools -> Facebook; the connection stays Tech-only, these
  // roles only use it. Reuses WebsiteContentPanel so the two can never drift.
  // Communications (AJ, 2026-10-12): News (was Post News), Welcome Letters (was
  // Draft Board > Welcome Letter), Birthday Reminders, Messaging (was the AD
  // Messaging tab / Scheduler sport sub-tab). See COMMUNICATIONS_HUB.md.
  { value: 'communications', label: 'Communications', roles: [SCH, AD], testid: 'tab-communications', activeClass: 'data-[state=active]:bg-teal-600' },
  // OL Finance — every Finance screen (this and later steps) renders inside this ONE tab.
  { value: 'finance',      label: 'Finance',         roles: [TR, AC], testid: 'tab-finance', activeClass: 'data-[state=active]:bg-emerald-600' },
  // AD Finance (AJ 2026-10-19, AD_DASHBOARD_AND_FINANCE.md §2) — Player Dues, Budget and
  // Reports (the Treasurer's read-only report viewer, was its own 'finance-reports' page).
  // Distinct from OL Finance above; the Treasurer sees it as "AD Finance".
  { value: 'ad-finance', label: (role) => (role === TR ? 'AD Finance' : 'Finance'), roles: [AD, TR], testid: 'tab-ad-finance', activeClass: 'data-[state=active]:bg-emerald-600' },
  { value: 'company',      label: 'Company',         roles: [CS] },
  // ALWAYS LAST (AJ 2026-10-14): History second-to-last, then Tools.
  // AD: History is the Statistics page's 2nd tab, not a menu (AJ 2026-10-19).
  { value: 'history',      label: 'History',         roles: [CS, LA], testid: 'tab-history' },
  { value: 'tools', label: 'Tools', roles: [CS, TS], testid: 'tab-tools', activeClass: 'data-[state=active]:bg-teal-600' },
  // Tools (AJ, 2026-10-12): Scorebook Log / Stickers / Season Program. Value is
  // 'ad-tools' because 'tools' is the Tech Specialist's Tools tab. SCOREBOOK.md.
  { value: 'ad-tools', label: 'Tools', roles: [SCH, AD], testid: 'tab-ad-tools', activeClass: 'data-[state=active]:bg-teal-600' },
];

/**
 * Per-role menu ORDER, for a role whose menu does not follow the shared array
 * order above (AJ 2026-10-19). Listed pages come first in this order; any other
 * page the role has keeps its NAV_ITEMS order after them. It ORDERS only — what a
 * role sees is still `roles` + the entitlement filter (a page not in the
 * customer's plan stays hidden wherever it would sort).
 */
export const ROLE_NAV_ORDER = {
  // Team Scheduler: Schedule, Communications, Contacts, Media, Registration, Teams, Tools.
  [SCH]: ['schedule', 'communications', 'contacts', 'site-media', 'registration-hub', 'teams-hub', 'ad-tools'],
  // Athletic Director (AJ 2026-10-19): Overview, Finance (AD Finance, 2nd), Communications,
  // Calendar, Teams, Registration, Members, Media, Statistics, Library, Volunteer, Tools.
  [AD]: ['overview', 'ad-finance', 'communications', 'calendar', 'teams-hub', 'registration-hub', 'contacts', 'documents',
    'site-media', 'stats', 'library', 'volunteer', 'ad-tools'],
};

/** Sort any list of NAV rows / pages for one role by ROLE_NAV_ORDER (stable). */
export const orderForRole = (role, rows) => {
  const order = ROLE_NAV_ORDER[role];
  if (!order) return rows;
  const rank = (r) => { const i = order.indexOf(r.value || r.id); return i === -1 ? order.length : i; };
  return rows.map((r, i) => [r, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([r]) => r);
};

/** The role's menu rows in its order. */
export const navItemsFor = (role) => orderForRole(role, NAV_ITEMS.filter((i) => i.roles.includes(role)));

/**
 * Category + feature gate per page.
 *
 * `category` groups pages the way the feature registry does, and the gate is
 * DERIVED from it (backend `utils/features.py :: category_feature_map`), so the
 * two cannot drift apart. A category with no feature key is UNGATED - Home,
 * Teams, Tools, Settings and the Semprion-only Platform pages stay available to
 * whoever their `roles` already allow.
 *
 * `featureKey` overrides the category gate for a single page.
 *
 * Keep the values in step with backend/utils/features.py. A page with an unknown
 * category is harmless (it renders ungated) but usually means the category
 * should have been added to the registry.
 */
export const PAGE_META = {
  // -- Ungated: core club operation, plus platform/staff surfaces --------
  overview:             { category: 'Home' },
  players:              { category: 'Home' },        // family: My Players
  'teams-hub':          { category: 'Teams' },
  teams:                { category: 'Teams' },       // coach view
  roster:               { category: 'Teams' },
  settings:             { category: 'Settings' },
  tools:                { category: 'Tools' },
  'ad-tools':           { category: 'Tools' },
  organizations:        { category: 'Platform' },
  seasons:              { category: 'Platform' },
  activity:             { category: 'Platform' },
  'hq-settings':        { category: 'Platform' },
  company:              { category: 'Platform' },
  history:              { category: 'Platform' },

  // -- Scheduling -------------------------------------------------------
  schedule:             { category: 'Scheduling' },
  booking:              { category: 'Scheduling' },
  calendar:             { category: 'Scheduling' },

  // -- Registration (incl. dues/payments, where OL bills families) ------
  'registration-hub':   { category: 'Registration' },
  registration:         { category: 'Registration' },  // family: Register
  payments:             { category: 'Registration' },

  // -- Finance (OL Finance — the Treasurer's books) ----------------------
  finance:              { category: 'Finance' },
  // AD Finance — its own category, gated by Player Fees & Invoicing (fees_invoicing),
  // NOT the Treasurer's bookkeeping engine (AJ: keep the two distinct).
  'ad-finance':         { category: 'AD Finance' },

  // -- Website (the public WP site and everything that feeds it) ---------
  'schedule-v3':        { category: 'Website' },       // WP Mgmt
  'tech-settings':      { category: 'Website' },
  'website-visibility': { category: 'Website' },
  'download-manager':   { category: 'Website' },
  'individual-awards':  { category: 'Website' },
  sponsors:             { category: 'Website' },

  // -- Media ------------------------------------------------------------
  'site-media':         { category: 'Media' },
  media:                { category: 'Media' },

  // -- Members ----------------------------------------------------------
  contacts:             { category: 'Members' },
  documents:            { category: 'Members' },
  families:             { category: 'Members' },
  'member-mgmt':        { category: 'Members' },

  // -- Communications ---------------------------------------------------
  communications:       { category: 'Communications' },
  messaging:            { category: 'Communications' },

  // -- Library (program documents; gated with Members by members_engine) --
  library:              { category: 'Library' },

  // -- Statistics & reporting -------------------------------------------
  stats:                { category: 'Statistics' },
  reports:              { category: 'Statistics' },
  exports:              { category: 'Statistics' },

  // -- Game Day ---------------------------------------------------------
  gameday:              { category: 'Game Day' },
  rsvp:                 { category: 'Game Day' },
  availability:         { category: 'Game Day' },
  lineup:               { category: 'Game Day' },
  'live-scoring':       { category: 'Game Day' },

  // -- Standalone add-ons -----------------------------------------------
  tournaments:          { category: 'Tournaments' },
  volunteer:            { category: 'Volunteer' },
};

/** The page rows with `id`, `category` and `featureKey` attached. */
export const PAGES = NAV_ITEMS.map((item) => {
  const meta = PAGE_META[item.value] || {};
  return {
    ...item,
    id: item.value,
    category: meta.category || 'Other',
    featureKey: meta.featureKey || null,
  };
});

/**
 * Every role that appears in the registry. DERIVED, never hardcoded: BOP
 * hardcodes its user-type list in the equivalent helper, which is how its whole
 * Facilities Manager role ended up absent from the catalog (defect D1).
 */
export const ROLE_TYPES = [...new Set(PAGES.flatMap((p) => p.roles))].sort();

/** Resolve a label that may be a function of the viewing role. */
export const pageLabel = (item, role) =>
  (typeof item.label === 'function' ? item.label(role) : item.label);

/** Rows for one role, in registry order. */
export const pagesFor = (role) => orderForRole(role, PAGES.filter((p) => p.roles.includes(role)));

/**
 * The payload POSTed to /api/view-config/catalog/sync.
 * `sort_order` is the row's index WITHIN ITS ROLE, so reordering NAV_ITEMS is
 * what reorders a menu - nothing else.
 */
export const catalogPayload = () => {
  const rows = [];
  ROLE_TYPES.forEach((role) => {
    pagesFor(role).forEach((p, idx) => {
      rows.push({
        role_type: role,
        id: p.id,
        label: typeof p.label === 'function' ? p.label(role) : p.label,
        category: p.category,
        feature_key: p.featureKey,
        sort_order: idx,
        testid: p.testid || `tab-${p.value}`,
      });
    });
  });
  return rows;
};

export default PAGES;
